/**
 * Color tokens.
 *
 * The actual OKLCH values + light/dark switching live in the web global
 * stylesheet (web/index.html) as CSS custom properties. Here we expose the
 * `var(--token)` references so StyleSheet rules resolve to the live variables —
 * this mirrors the source exactly (every color was a `var(--…)`) and makes
 * theme switching automatic via `data-theme` on <html>, no JS re-render needed.
 *
 * On native (no CSS vars) these strings would need a resolver; web is the
 * target surface for this clone.
 */

const v = (name: string) => `var(--${name})`;

export const colors = {
  paper: v('paper'),
  paper2: v('paper-2'),
  paper3: v('paper-3'),
  card: v('card'),

  ink: v('ink'),
  ink2: v('ink-2'),
  ink3: v('ink-3'),
  ink4: v('ink-4'),

  line: v('line'),
  lineStrong: v('line-strong'),

  accent: v('accent'),
  accentInk: v('accent-ink'),
  accentSoft: v('accent-soft'),
  accentLine: v('accent-line'),

  info: v('info'),
  infoSoft: v('info-soft'),
  infoLine: v('info-line'),

  success: v('success'),
  successSoft: v('success-soft'),
  successLine: v('success-line'),

  warn: v('warn'),
  warnSoft: v('warn-soft'),
  warnLine: v('warn-line'),

  danger: v('danger'),
  dangerSoft: v('danger-soft'),
  dangerLine: v('danger-line'),

  neutral: v('neutral'),
  neutralSoft: v('neutral-soft'),
  neutralLine: v('neutral-line'),

  // fixed (non-token) values used verbatim in the source
  white: '#fff',
  overlay: 'rgba(16,24,40,0.42)',
  overlaySoft: 'rgba(16,24,40,0.32)',
  knobShadow: '0 1px 2px rgba(16,24,40,0.25)',
} as const;

/** Semantic tone keys used by pills, badges, bars (tone → token family). */
export type Tone = 'info' | 'success' | 'warn' | 'danger' | 'neutral' | 'accent';

/** Build the three CSS var refs for a semantic tone family. */
export const tone = (t: string) => ({
  fg: `var(--${t})`,
  soft: `var(--${t}-soft)`,
  line: `var(--${t}-line)`,
});

export type Colors = typeof colors;
